package rendering

import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.dp
import domain.COLORS
import engine.model.GameSnapshot
import engine.model.Matrix
import engine.model.PieceState

// ── Constants ─────────────────────────────────────────────────────────────────

/** Pixel size of one board cell. */
const val BLOCK_SIZE = 30f

/** Number of visible rows (excludes buffer zone). */
const val BOARD_ROWS = 20

/** Number of columns. */
const val BOARD_COLS = 10

/** Buffer rows above the visible area (pieces spawn here). */
const val BOARD_BUFFER = 4

// ── Color helpers ─────────────────────────────────────────────────────────────

/** Convert a packed 0xRRGGBB integer to a Compose [Color]. */
private fun Int.toColor(alpha: Float = 1f): Color {
    val r = ((this shr 16) and 0xFF) / 255f
    val g = ((this shr 8) and 0xFF) / 255f
    val b = (this and 0xFF) / 255f
    return Color(r, g, b, alpha)
}

private val BORDER_COLOR = Color(0xFF_22_22_44.toInt())
private val GRID_COLOR = Color(0xFF_11_11_22.toInt())
private val GHOST_FILL_ALPHA = 0.12f
private val GHOST_STROKE_ALPHA = 0.55f
private const val STICK_BAR_ALPHA = 0.45f
private const val STICK_BAR_HEIGHT = 3f

// ── BoardCanvas ───────────────────────────────────────────────────────────────

/**
 * Renders the game board using Compose [DrawScope].
 *
 * Draws (in order):
 * 1. Grid lines for empty cells
 * 2. Placed blocks with highlight / shadow edges
 * 3. Ghost piece (outlined cells with low-alpha fill)
 * 4. Current falling piece
 * 5. Stick-piece entry indicator bar (when piece is in buffer zone)
 * 6. Board border
 *
 * Board effects (shake, tilt, wiggle, lock flash) are applied via
 * [Modifier.graphicsLayer] on the caller side — not inside this composable.
 *
 * @param snapshot       Latest [GameSnapshot] from the engine.
 * @param modifier       Modifier applied to the [Canvas] composable.
 * @param blockSize      Size of one board cell in dp-pixels. Defaults to [BLOCK_SIZE].
 *                       Pass a computed value for responsive layouts.
 * @param debrisState    Optional [DebrisState] for line-clear particle effects.
 * @param lockFlashColor When non-null, overrides the board border colour for a
 *                       lock-flash effect.
 */
@Composable
fun BoardCanvas(
    snapshot: GameSnapshot,
    modifier: Modifier = Modifier,
    blockSize: Float = BLOCK_SIZE,
    debrisState: DebrisState? = null,
    lockFlashColor: Color? = null,
) {
    Canvas(modifier = modifier) {
        val bs = blockSize

        // 1. Grid lines
        drawGrid(bs)

        // 2. Placed blocks
        drawBoard(snapshot.board.contents, snapshot.board.bufferSize, bs)

        // 3. Ghost piece
        snapshot.ghostPiece?.let { drawGhostPiece(it, snapshot.board.bufferSize, bs) }

        // 4. Current piece
        snapshot.currentPiece?.let { drawPiece(it, snapshot.board.bufferSize, bs) }

        // 5. Stick-piece entry indicator
        snapshot.currentPiece?.let { drawStickPiece(it, snapshot.board.bufferSize, bs) }

        // 6. Debris particles
        debrisState?.let { drawDebris(it) }

        // 7. Board border (with optional lock flash)
        drawBoardBorder(bs, lockFlashColor)
    }
}

// ── Grid ──────────────────────────────────────────────────────────────────────

private fun DrawScope.drawGrid(bs: Float) {
    // Vertical lines
    for (c in 0..BOARD_COLS) {
        val x = c * bs
        drawLine(
            color = GRID_COLOR,
            start = Offset(x, 0f),
            end = Offset(x, BOARD_ROWS * bs),
            strokeWidth = 0.5f,
        )
    }
    // Horizontal lines
    for (r in 0..BOARD_ROWS) {
        val y = r * bs
        drawLine(
            color = GRID_COLOR,
            start = Offset(0f, y),
            end = Offset(BOARD_COLS * bs, y),
            strokeWidth = 0.5f,
        )
    }
}

// ── Board cells ───────────────────────────────────────────────────────────────

private fun DrawScope.drawBoard(matrix: Matrix, bufferSize: Int, bs: Float) {
    for (r in bufferSize until matrix.rows) {
        val visibleRow = r - bufferSize
        for (c in 0 until matrix.cols) {
            val cellId = matrix[r, c]
            if (cellId == 0) continue
            val color = (COLORS[cellId] ?: 0x888888).toColor()
            drawCell(visibleRow, c, color, bs)
        }
    }
}

/**
 * Draws a filled block with highlight (top/left) and shadow (bottom/right) edges.
 * Three rectangles per cell: base fill, highlight strip, shadow strip.
 */
private fun DrawScope.drawCell(row: Int, col: Int, color: Color, bs: Float) {
    val x = col * bs
    val y = row * bs
    val edgeW = (bs * 0.12f).coerceAtLeast(1.5f)

    // Base fill
    drawRect(color = color, topLeft = Offset(x, y), size = Size(bs, bs))

    // Highlight (top + left edges — brighter)
    val highlight = Color(
        red = (color.red + 0.25f).coerceAtMost(1f),
        green = (color.green + 0.25f).coerceAtMost(1f),
        blue = (color.blue + 0.25f).coerceAtMost(1f),
        alpha = 0.7f,
    )
    // Top edge
    drawRect(color = highlight, topLeft = Offset(x, y), size = Size(bs, edgeW))
    // Left edge
    drawRect(color = highlight, topLeft = Offset(x, y), size = Size(edgeW, bs))

    // Shadow (bottom + right edges — darker)
    val shadow = Color(
        red = (color.red - 0.25f).coerceAtLeast(0f),
        green = (color.green - 0.25f).coerceAtLeast(0f),
        blue = (color.blue - 0.25f).coerceAtLeast(0f),
        alpha = 0.7f,
    )
    // Bottom edge
    drawRect(color = shadow, topLeft = Offset(x, y + bs - edgeW), size = Size(bs, edgeW))
    // Right edge
    drawRect(color = shadow, topLeft = Offset(x + bs - edgeW, y), size = Size(edgeW, bs))
}

// ── Ghost piece ───────────────────────────────────────────────────────────────

private fun DrawScope.drawGhostPiece(ghost: PieceState, bufferSize: Int, bs: Float) {
    val color = (COLORS[ghost.type.id] ?: 0x888888).toColor()
    for (r in 0 until ghost.shape.rows) {
        for (c in 0 until ghost.shape.cols) {
            if (ghost.shape[r, c] == 0) continue
            val boardRow = ghost.row + r - bufferSize
            if (boardRow < 0) continue
            val x = (ghost.col + c) * bs
            val y = boardRow * bs
            drawOutlinedCell(x, y, color, bs)
        }
    }
}

/**
 * Draws a ghost cell: low-alpha fill + stroke outline.
 */
private fun DrawScope.drawOutlinedCell(x: Float, y: Float, color: Color, bs: Float) {
    // Low-alpha fill
    drawRect(
        color = color.copy(alpha = GHOST_FILL_ALPHA),
        topLeft = Offset(x, y),
        size = Size(bs, bs),
    )
    // Stroke outline
    drawRect(
        color = color.copy(alpha = GHOST_STROKE_ALPHA),
        topLeft = Offset(x, y),
        size = Size(bs, bs),
        style = Stroke(width = 1.5f),
    )
}

// ── Current piece ─────────────────────────────────────────────────────────────

private fun DrawScope.drawPiece(piece: PieceState, bufferSize: Int, bs: Float) {
    val color = (COLORS[piece.type.id] ?: 0x888888).toColor()
    for (r in 0 until piece.shape.rows) {
        for (c in 0 until piece.shape.cols) {
            if (piece.shape[r, c] == 0) continue
            val boardRow = piece.row + r - bufferSize
            if (boardRow < 0) continue
            drawCell(boardRow, piece.col + c, color, bs)
        }
    }
}

// ── Stick-piece entry indicator ───────────────────────────────────────────────

/**
 * Draws a thin indicator bar at the top of the visible board when the current
 * piece is still in the buffer zone (row < bufferSize).
 *
 * Only the columns occupied by the piece are lit, using the piece's own colour,
 * so the bar matches the piece width and hue rather than spanning the full board.
 */
private fun DrawScope.drawStickPiece(piece: PieceState, bufferSize: Int, bs: Float) {
    // Collect the board-columns that the piece occupies
    val occupiedCols = mutableSetOf<Int>()
    var anyInBuffer = false
    for (r in 0 until piece.shape.rows) {
        for (c in 0 until piece.shape.cols) {
            if (piece.shape[r, c] == 0) continue
            val boardRow = piece.row + r
            if (boardRow < bufferSize) anyInBuffer = true
            occupiedCols.add(piece.col + c)
        }
    }
    if (!anyInBuffer || occupiedCols.isEmpty()) return

    val pieceColor = (COLORS[piece.type.id] ?: 0xFFFFFF).toColor(alpha = STICK_BAR_ALPHA)

    // Draw one thin rect per occupied column so the bar exactly matches the piece footprint
    for (col in occupiedCols) {
        if (col < 0 || col >= BOARD_COLS) continue
        drawRect(
            color = pieceColor,
            topLeft = Offset(col * bs, 0f),
            size = Size(bs, STICK_BAR_HEIGHT),
        )
    }
}

// ── Board border ──────────────────────────────────────────────────────────────

private fun DrawScope.drawBoardBorder(bs: Float, flashColor: Color?) {
    val borderColor = flashColor ?: BORDER_COLOR
    drawRect(
        color = borderColor,
        topLeft = Offset(0f, 0f),
        size = Size(BOARD_COLS * bs, BOARD_ROWS * bs),
        style = Stroke(width = 2f),
    )
}
